<script lang="ts">
  import type { LeaderboardEntry, LeaderboardPage, LeaderboardScope } from '@2k48/shared';
  import { animateTabPanel } from '../../game/motion';
  import TopBar from '../components/TopBar.svelte';
  import PixelAvatar from '../components/PixelAvatar.svelte';
  import PixelIcon from '../components/PixelIcon.svelte';
  import WaveText from '../components/WaveText.svelte';
  import PixelTabs from '../components/PixelTabs.svelte';
  import { i18n } from '../i18n/index.svelte';
  import { session } from '../stores/session.svelte';
  import { router } from '../stores/router.svelte';
  import { api } from '../../net/client';
  import { groupNumber, signed } from '../format';

  /**
   * Two boards that measure different things, as the mockups have them: versus
   * ranks by Elo and leads with a podium, global ranks by score and leads with
   * the record holder.
   *
   * Both are the same endpoint with different coordinates — `/api/rankings` is
   * (metric x scope x mode) — which is why adding "weekly versus" later is a
   * new argument here rather than a new screen.
   */

  type BoardId = 'versus' | 'global';

  const BOARDS: Record<BoardId, { metric: 'rating' | 'score'; mode: string; unit: string }> = {
    versus: { metric: 'rating', mode: 'versus', unit: 'ELO' },
    global: { metric: 'score', mode: 'solo', unit: 'SCORE' },
  };

  const t = $derived(i18n.t);
  const SCOPES = $derived<{ id: LeaderboardScope; label: string }[]>([
    { id: 'global', label: t.rankings.all },
    { id: 'weekly', label: t.rankings.scopeWeekly },
    { id: 'daily', label: t.rankings.scopeDaily },
  ]);

  let board = $state<BoardId>('versus');
  /**
   * Only the Elo board carries a win/loss record. On the score board the column
   * would be a versus statistic sitting next to a solo score — two unrelated
   * numbers in one row.
   */
  const showsRecord = $derived(board === 'versus');
  /** The score board shows the biggest tile behind each record, as the mockup does. */
  const showsTile = $derived(board === 'global');
  let scope = $state<LeaderboardScope>('global');
  let page = $state<LeaderboardPage | null>(null);
  let loading = $state(true);
  let failed = $state(false);

  $effect(() => {
    const currentBoard = board;
    const currentScope = scope;
    let cancelled = false;

    loading = true;
    void fetchBoard(currentBoard, currentScope)
      .then((result) => {
        if (cancelled) return;
        page = result;
        failed = false;
      })
      .catch(() => {
        if (cancelled) return;
        page = null;
        failed = true;
      })
      .finally(() => {
        if (!cancelled) loading = false;
      });

    return () => {
      cancelled = true;
    };
  });

  function fetchBoard(id: BoardId, currentScope: LeaderboardScope): Promise<LeaderboardPage> {
    const { metric, mode } = BOARDS[id];
    const query = [`metric=${metric}`, `mode=${mode}`, `scope=${currentScope}`, 'limit=50'];
    // Asking for our own row explicitly is what lets the board show "you are
    // 42nd" without paging through 41 rows to find out.
    if (session.profile) query.push(`player=${encodeURIComponent(session.profile.id)}`);
    return api<LeaderboardPage>(`/rankings?${query.join('&')}`);
  }

  const entries = $derived(page?.entries ?? []);
  const podium = $derived(entries.slice(0, 3));
  /** The podium already shows the top three, so the list starts after them. */
  const listed = $derived(board === 'versus' ? entries.slice(3) : entries.slice(1));
  const champion = $derived<LeaderboardEntry | undefined>(entries[0]);
  const self = $derived(page?.self ?? null);
  const selfListed = $derived(
    self !== null && entries.some((entry) => entry.playerId === self.playerId),
  );
  const weeklyRatingDelta = $derived(page?.selfWeeklyRatingDelta ?? 0);
</script>

<div class="view rankings">
  <TopBar title={t.rankings.title} />

  <div class="view__body">
    <PixelTabs
      class="rankings__tabs"
      label={t.rankings.ariaBoard}
      value={board}
      options={[
        { id: 'versus', label: t.rankings.versus },
        { id: 'global', label: t.rankings.scopeGlobal },
      ]}
      onchange={(value) => (board = value as BoardId)}
    />

    <PixelTabs
      class="rankings__tabs"
      label={t.rankings.ariaScope}
      value={scope}
      options={SCOPES}
      onchange={(value) => (scope = value as LeaderboardScope)}
    />

    {#if loading}
      <p class="view__placeholder">{t.common.loading}</p>
    {:else if failed}
      <p class="view__placeholder">{t.rankings.offlineMessage}</p>
    {:else if entries.length === 0}
      <p class="view__placeholder">{t.rankings.emptyMessage}</p>
    {:else}
      {#key `${board}:${scope}`}
        <div class="rankings__content" use:animateTabPanel>
          {#if board === 'versus'}
            <!-- Second, first, third: the champion sits in the middle and taller,
             which is what makes it read as a podium without any label. -->
            <div class="podium">
              {#each [podium[1], podium[0], podium[2]] as slot, index (index)}
                <button
                  type="button"
                  class="podium__slot podium__slot--{index === 1 ? 'first' : 'other'} profile-entry"
                  disabled={!slot}
                  onclick={() => slot && router.go('profile', { player: slot.playerId })}
                >
                  {#if slot}
                    <PixelIcon
                      name={index === 1 ? 'crown' : index === 0 ? 'medal-silver' : 'medal-bronze'}
                      size={index === 1 ? 64 : 48}
                    />
                    <div class="podium__block">
                      <span class="podium__name">{slot.displayName}</span>
                      <!-- Ondula la cifra y no el nombre: un nombre necesita
                           truncarse con elipsis, y eso exige `overflow: hidden`,
                           que recortaba las letras justo cuando la ola las
                           levanta. El número es corto, no se trunca nunca, y es
                           lo que de verdad se está celebrando. -->
                      {#if index === 1}
                        <WaveText class="podium__value" text={groupNumber(slot.value)} />
                      {:else}
                        <span class="podium__value">{groupNumber(slot.value)}</span>
                      {/if}
                    </div>
                  {/if}
                </button>
              {/each}
            </div>
          {:else if champion}
            <button
              type="button"
              class="panel panel--accent rankings__champion profile-entry"
              onclick={() => router.go('profile', { player: champion.playerId })}
            >
              <PixelIcon name="crown" size={56} />
              <span class="rankings__champion-copy">
                <span class="rankings__champion-name">{champion.displayName}</span>
                <span class="tiny">{t.rankings.worldRecord}</span>
              </span>
              <WaveText class="rankings__champion-value" text={groupNumber(champion.value)} />
            </button>
          {/if}

          <div
            class="rankings__head"
            class:rankings__head--record={showsRecord}
            class:rankings__head--tile={showsTile}
            aria-hidden="true"
          >
            <span>{t.common.position}</span>
            <span></span>
            <span>{t.common.playerColumn}</span>
            {#if showsRecord}<span>{t.common.winsLosses}</span>{/if}
            {#if showsTile}<span>{t.common.max}</span>{/if}
            <span>{BOARDS[board].unit}</span>
          </div>

          <div class="rows rankings__list">
            {#each listed as entry (entry.playerId)}
              <button
                type="button"
                class="row row--ranked {self && entry.playerId === self.playerId
                  ? 'row--self'
                  : ''} profile-entry"
                data-rank={entry.rank}
                onclick={() => router.go('profile', { player: entry.playerId })}
              >
                <span class="row__rank {entry.rank <= 3 ? 'row__rank--top' : ''}">{entry.rank}</span
                >
                <PixelAvatar avatarId={entry.avatarId} size="sm" />
                <span class="row__name">{entry.displayName}</span>
                {#if showsRecord}
                  <span class="row__record" data-positive={entry.versusWins >= entry.versusLosses}>
                    {entry.versusWins}/{entry.versusLosses}
                  </span>
                {/if}
                {#if showsTile}
                  <span class="row__tile">
                    {#if entry.maxTile}<span class="badge badge--cyan">{entry.maxTile}</span>{/if}
                  </span>
                {/if}
                <span class="row__value">{groupNumber(entry.value)}</span>
              </button>
            {/each}
          </div>

          {#if self && !selfListed}
            <p class="rankings__gap" aria-hidden="true">· · ·</p>
            <div class="rows">
              <button
                type="button"
                class="row row--ranked row--self profile-entry"
                onclick={() => router.go('profile', { player: self.playerId })}
              >
                <span class="row__rank">{self.rank}</span>
                <PixelAvatar avatarId={self.avatarId} size="sm" />
                <span class="row__name">{self.displayName}</span>
                {#if showsRecord}
                  <span class="row__record" data-positive={self.versusWins >= self.versusLosses}>
                    {self.versusWins}/{self.versusLosses}
                  </span>
                {/if}
                {#if showsTile}
                  <span class="row__tile">
                    {#if self.maxTile}<span class="badge badge--cyan">{self.maxTile}</span>{/if}
                  </span>
                {/if}
                <span class="row__value">{groupNumber(self.value)}</span>
              </button>
            </div>
          {/if}

          {#if board === 'versus' && self}
            <p
              class="rankings__weekly-delta"
              data-direction={weeklyRatingDelta > 0
                ? 'up'
                : weeklyRatingDelta < 0
                  ? 'down'
                  : 'flat'}
            >
              <i class="rankings__trend-arrow" aria-hidden="true"></i>
              <span>{signed(weeklyRatingDelta)} ESTA SEMANA</span>
            </p>
          {/if}
        </div>
      {/key}
    {/if}
  </div>
</div>
